import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { db } from "~/db";
import { footer, sysFile, sysFileReference, ttContent } from "~/db/schema";
import { pages } from "~/db/schema/typo3/pages.schema";

type ContentLoader<TItem = any, TResult = any> = (
  item: TItem,
) => Promise<TResult>;

const contentLoaders: Record<string, ContentLoader> = {
  footer: loadFooter,
};

async function loadFooter(item: typeof footer.$inferSelect) {
  const [footerElement] = await db
    .select({
      supportText: footer.supportText,
      phonenumber: footer.phonenumber,
      email: footer.email,
      brandMessage: footer.brandMessage,
      logoIdentifier: sysFile.identifier,
    })
    .from(footer)
    .leftJoin(
      sysFileReference,
      and(
        eq(sysFileReference.uidForeign, footer.uid),
        eq(sysFileReference.fieldname, "pnodev_footer_logo"),
        eq(sysFileReference.deleted, 0),
        eq(sysFileReference.hidden, 0),
      ),
    )
    .leftJoin(sysFile, eq(sysFile.uid, sysFileReference.uidLocal))
    .where(
      and(
        eq(footer.uid, item.uid),
        eq(footer.deleted, 0),
        eq(footer.hidden, 0),
      ),
    );

  const linksUpper = await db
    .select()
    .from(pages)
    .where(and(eq(pages.pid, 9), eq(pages.deleted, 0), eq(pages.hidden, 0)))
    .execute();

  const linksLower = await db
    .select()
    .from(pages)
    .where(and(eq(pages.pid, 10), eq(pages.deleted, 0), eq(pages.hidden, 0)))
    .execute();

  return {
    ...footerElement,
    linksUpper,
    linksLower,
  };
}

export type LoadedContentItem<TData = any> = {
  uid: number;
  pid: number;
  ctype: string;
  foreignTableParentUid: number | null;
  deleted: number;
  hidden: number;

  data: TData | null;
  children: LoadedContentItem<TData>[];
};

export const getFooter = createServerFn({ method: "GET" }).handler(
  async (ctx): Promise<LoadedContentItem | null> => {
    const page = await db
      .select()
      .from(pages)
      .where(
        and(
          eq(pages.uid, 8),
          eq(pages.deleted, 0),
          eq(pages.hidden, 0),
          eq(pages.doktype, 254),
        ),
      );

    if (page.length === 0) {
      return null;
    }
    const pid = page[0].uid;

    const [footerElement] = await db
      .select()
      .from(ttContent)
      .where(
        and(
          eq(ttContent.pid, pid),
          eq(ttContent.ctype, "footer"),
          eq(ttContent.deleted, 0),
          eq(ttContent.hidden, 0),
          eq(ttContent.tx_container_parent, 0),
        ),
      )
      .execute();

    if (!footerElement) return null;

    const loader = contentLoaders[footerElement.ctype];
    const data = loader ? await loader(footerElement) : null;

    return {
      ...footerElement,
      data,
      children: [],
    };
  },
);
